// main.tsx
import React from 'react';
import App from './App';
import './index.css';
import { createRoot } from "react-dom/client";
import "./instrument.js";
import ErrorFallback from './components/ErrorFallback';

import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react'
import * as Sentry from '@sentry/react';

// Detect if we're likely in Telegram Mini App context
// The SDK is loaded synchronously in index.html, so we just check for its presence
const isTelegramContext = !!(window.Telegram?.WebApp?.initData) ||
    window.parent !== window ||
    window.location.search.includes('tgWebAppData') ||
    window.location.hash.includes('tgWebAppData');

// PostHog initialization is now handled by the analytics service
// which initializes with the correct key based on appType

const isDevelopment = import.meta.env.VITE_ENVIRONMENT !== 'production';

// Unsupported browsers (e.g. iOS/Safari < 16.4 without RegExp lookbehind) are
// caught by the capability gate in index.html, which shows an upgrade message and
// sets this flag. Skip booting the app so we never hit the crashing markdown render.
const isUnsupportedBrowser = !!(window as Window & { __UNSUPPORTED_BROWSER__?: boolean }).__UNSUPPORTED_BROWSER__;

if (isUnsupportedBrowser) {
    // Report blocked users so we can monitor how many are affected and judge
    // whether the iOS 16.4 baseline is worth keeping. Sentry is already initialized
    // (instrument.js, imported above) and works on these devices.
    Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTags({
            unsupported_browser: 'true',
            missing_feature: 'regexp_lookbehind',
        });
        scope.setContext('browser_capability', {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
        });
        Sentry.captureMessage('Unsupported browser blocked (RegExp lookbehind unsupported)');
    });
} else {
const container = document.getElementById("root")!;
const root = createRoot(container, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        if (isDevelopment) {
            console.error('Uncaught error:', error);
            console.error('Component stack:', errorInfo.componentStack);
        }
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        if (isDevelopment) {
            console.error('Caught error:', error);
            console.error('Component stack:', errorInfo.componentStack);
        }
    }),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
        if (isDevelopment) {
            console.warn('Recoverable error:', error);
            console.warn('Component stack:', errorInfo.componentStack);
        }
    }),
});
root.render(
    <PostHogProvider client={posthog}>
        <React.StrictMode>
            <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                <App isTelegramContext={isTelegramContext} />
            </Sentry.ErrorBoundary>
        </React.StrictMode>
    </PostHogProvider>
);
}
